import { Injectable } from "@angular/core";

import { interval, Subscription, Subject, lastValueFrom } from "rxjs";
import { takeWhile } from "rxjs/operators";

import { AngularFirestore } from "@angular/fire/compat/firestore";
import { DatabaseReference, getDatabase, ref, set } from "firebase/database";

import { CONFIG } from "@constants/config";

import { UpgradeFwStatusEvent } from "connectapi/src/ApiMessage/apiResponseTypes";

/***************interfaces*********************** */

import { IAlarm } from "@interfaces/alarm.interface";
import {
  ICurrentDevice,
  IHardwareVersion,
  IParameterData,
  IPeriodicData,
  stringparametertemp,
} from "@interfaces/device.interface";

import { IAlarmLog } from "@shared/interfaces/alarm-log.interface";
import { IFirmwareFile } from "@shared/interfaces/firmware.interface";

/*****************************Queries************************************ */

import { AlarmQuery } from "@state/alarm/alarm.query";
import { DeviceQuery } from "@state/device/device.query";
import { UserQuery } from "@shared/state/user/user.query";

/**********************Services*********************************** */
import { ModalService } from "@services/modal.service";
import { DevicePresenceService } from "@services/device-presence.service";
import { promise } from "protractor";

/**************************************************************************** */

@Injectable({
  providedIn: "root",
})
export class FirestoreService {
  public duplicateDeviceDetected$ = new Subject<any>();
  private address: string | undefined = undefined;

  private tcuAddress: string = "";
  private ccuAddress: string = "";
  private hmiAddress: string = "";

  private blueToothConnectorsTotal: string[] = [];

  private connectedStatus!: string;

  private isConnected = false;

  private lastConnectedIsSet = false;

  private firstTcuUploadDone = false;
  private firstCcuUploadDone = false;
  private firstHmiUploadDone = false;

  private currentDeviceSub?: Subscription;
  private connectionSubs = new Subscription();
  private firebaseInterval!: Subscription;
  private updateFirebaseSubscription!: Subscription | undefined;
  private linkStop$ = new Subject<void>();
  private oldAlarms: IAlarm[];

  private oldAlarmLog!: IAlarmLog[];

  private oldHardwareVersions: IHardwareVersion[] = [];
  private oldCurrentDevice!: ICurrentDevice;

  private oldParameterData!: IParameterData[];
  private oldStringParameterData!: stringparametertemp[];

  private oldPeriodicData!: IPeriodicData[];

  // 24 timmar
  private timerMs = 86400000;

  private databaseRefPeriodicDataTcu!: DatabaseReference;
  private databaseRefLastConnectedTcu!: DatabaseReference;
  private databaseRefLastConnectedUserTcu!: DatabaseReference;

  private databaseRefPeriodicDataCcu!: DatabaseReference;
  private databaseRefLastConnectedCcu!: DatabaseReference;
  private databaseRefLastConnectedUserCcu!: DatabaseReference;

  // HMI HAS PERIODIC DATA
  private databaseRefPeriodicDataHmi!: DatabaseReference;
  private databaseRefLastConnectedHmi!: DatabaseReference;
  private databaseRefLastConnectedUserHmi!: DatabaseReference;

  public forceFlag = false;

  private TCUIsInactive = false;
  private CCUIsInactive = false;
  private HMIIsInactive = false;

  //adding these flags to make sure that there is no multiple active entries of these units in firebase

  public TCUExistsAlready = true;
  public CCUExistsAlready = true;
  public HMIExistsAlready = true;

  private presenceStartedFor = new Set<string>(); // tracks which deviceIds we marked "connected"
  //private userDevicesRef;

  constructor(
    private angularFirestore: AngularFirestore,
    private deviceQuery: DeviceQuery,
    private alarmQuery: AlarmQuery,
    private userQuery: UserQuery,
    private modalService: ModalService,
    private devicePresence: DevicePresenceService,
  ) {
    this.oldAlarms = [];

    this.deviceQuery.currentDevice$?.subscribe((res) => {
      if (res?.status === ("connected" as string)) {
        // reset the per-connection listeners to avoid stacking
        this.connectionSubs.unsubscribe();
        this.connectionSubs = new Subscription();
        this.connectedStatus = res.status;

        const db = getDatabase();

        if (res?.tcuAddress) {
          this.tcuAddress = res.tcuAddress;
          // console.log(this.tcuAddress, res.tcuAddress);
          this.databaseRefPeriodicDataTcu = ref(
            db,
            "/devices/" + this.tcuAddress + "/periodicData",
          );
          this.databaseRefLastConnectedTcu = ref(
            db,
            "/devices/" + this.tcuAddress + "/lastConnected",
          );
          this.databaseRefLastConnectedUserTcu = ref(
            db,
            "/devices/" + this.tcuAddress + "/lastConnectedUser",
          );

          // NEW: mark presence for TCU
          this.startPresenceIfNeeded(this.tcuAddress);

          if (!this.userQuery.notConnectedToDevice) {
            this.checkForDuplicateTcu(this.tcuAddress);
          }
          this.connectionSubs.add(
            this.handleIfDeviceBecomesInactive(this.tcuAddress).subscribe(
              (doc) => {
                // console.log('DEVICE', doc);
                if (doc && doc.isInactive !== undefined) {
                  if (doc.isInactive) {
                    this.TCUIsInactive = true;
                  } else {
                    this.TCUIsInactive = false;
                  }

                  this.deviceQuery.toggleDeviceInactivity(
                    doc.ecuId,
                    this.TCUIsInactive,
                  );
                }
              },
            ),
          );
        } else {
          this.TCUExistsAlready = false;
        }

        if (res?.ccuAddress) {
          this.ccuAddress = res.ccuAddress;
          this.databaseRefPeriodicDataCcu = ref(
            db,
            "/devices/" + this.ccuAddress + "/periodicData",
          );
          this.databaseRefLastConnectedCcu = ref(
            db,
            "/devices/" + this.ccuAddress + "/lastConnected",
          );
          this.databaseRefLastConnectedUserCcu = ref(
            db,
            "/devices/" + this.ccuAddress + "/lastConnectedUser",
          );

          // NEW: mark presence for CCU
          this.startPresenceIfNeeded(this.ccuAddress);

          if (!this.userQuery.notConnectedToDevice) {
            this.checkForDuplicateCcu(this.ccuAddress);
          }
          this.connectionSubs.add(
            this.handleIfDeviceBecomesInactive(this.ccuAddress).subscribe(
              (doc) => {
                //console.log('DEVICE', doc);

                if (doc && doc.isInactive !== undefined) {
                  if (doc.isInactive) {
                    this.CCUIsInactive = true;
                  } else {
                    this.CCUIsInactive = false;
                  }

                  this.deviceQuery.toggleDeviceInactivity(
                    doc.ecuId,
                    this.CCUIsInactive,
                  );
                }
              },
            ),
          );
        } else {
          this.CCUExistsAlready = false;
        }
        if (res?.hmiAddress) {
          this.hmiAddress = res.hmiAddress;
          this.databaseRefPeriodicDataHmi = ref(
            db,
            "/devices/" + this.hmiAddress + "/periodicData",
          );
          this.databaseRefLastConnectedHmi = ref(
            db,
            "/devices/" + this.hmiAddress + "/lastConnected",
          );
          this.databaseRefLastConnectedUserHmi = ref(
            db,
            "/devices/" + this.hmiAddress + "/lastConnectedUser",
          );

          // NEW: mark presence for HMI
          this.startPresenceIfNeeded(this.hmiAddress);

          if (!this.userQuery.notConnectedToDevice) {
            this.checkForDuplicateHmi(this.hmiAddress);
          }
          this.connectionSubs.add(
            this.handleIfDeviceBecomesInactive(this.hmiAddress).subscribe(
              (doc) => {
                // console.log('DEVICE', doc);
                if (doc && doc.isInactive !== undefined) {
                  if (doc.isInactive) {
                    this.HMIIsInactive = true;
                  } else {
                    this.HMIIsInactive = false;
                  }

                  this.deviceQuery.toggleDeviceInactivity(
                    doc.ecuId,
                    this.HMIIsInactive,
                  );
                }
              },
            ),
          );
        } else {
          this.HMIExistsAlready = false;
        }

        this.address = res.address;
        this.isConnected = true;
      } else if (res?.status === "offline") {
        this.connectedStatus = res.status;

        if (res?.tcuAddress) {
          this.tcuAddress = res.tcuAddress;
          // NEW: flip device presence to disconnected (all that might exist)
          this.stopPresenceIfNeeded(res?.tcuAddress || this.tcuAddress);
          this.removePresenceIfNeeded(res?.tcuAddress || this.tcuAddress);
        }

        if (res?.ccuAddress) {
          this.ccuAddress = res.ccuAddress;
          this.stopPresenceIfNeeded(res?.ccuAddress || this.ccuAddress);
          this.removePresenceIfNeeded(res?.ccuAddress || this.ccuAddress);
        }

        if (res?.hmiAddress) {
          this.hmiAddress = res.hmiAddress;
          this.stopPresenceIfNeeded(res?.hmiAddress || this.hmiAddress);
          this.removePresenceIfNeeded(res?.hmiAddress || this.hmiAddress);
        }

        this.address = res.address;
      } else {
        this.isConnected = false;
        this.removeAllPresenceIfStarted();
        this.updateLatestConnection(this.userQuery.userUid);
        this.stopAllPresence();
      }
    });
  }

  private async startPresenceIfNeeded(id?: string) {
    if (!id || this.presenceStartedFor.has(id)) return;
    await this.devicePresence.start(id); // optional meta
    this.presenceStartedFor.add(id);
  }

  private async removePresenceIfNeeded(id?: string) {
    if (!id || !this.presenceStartedFor.has(id)) return;
    await this.devicePresence.removeMapping(id); // calls stop(..., { deleteMapping:true })
    this.presenceStartedFor.delete(id);
  }

  private async stopPresenceIfNeeded(id?: string) {
    if (!id || !this.presenceStartedFor.has(id)) return; // 👈 prevents accidental stop-writes
    await this.devicePresence.stop(id);
    this.presenceStartedFor.delete(id);
  }
  private async removeAllPresenceIfStarted() {
    await Promise.all(
      [...this.presenceStartedFor].map((id) =>
        this.devicePresence.removeMapping(id),
      ),
    );
    this.presenceStartedFor.clear();
  }

  public async removeConnectedDevices() {
    await this.removeAllPresenceIfStarted();
    await this.devicePresence.removeConnectionDevice();
  }

  private async stopAllPresence() {
    // belt & suspenders
    await Promise.all(
      [...this.presenceStartedFor].map((id) => this.devicePresence.stop(id)),
    );
    this.presenceStartedFor.clear();
  }

  public startFirestoreLink() {
    this.firstTcuUploadDone = false;
    this.firstCcuUploadDone = false;
    this.firstHmiUploadDone = false;

    // stop any previous link first
    this.cancelFirestoreLink();

    // (re)create the stop signal for this session
    this.linkStop$ = new Subject<void>();

    // x(second) * 60(min) * 1000(milliseconds)
    // const ms = 30000;
    const ms = 5000;
    this.updateFirebaseSubscription = interval(ms).subscribe(() => {
      if (
        (this.tcuAddress || this.ccuAddress || this.hmiAddress) &&
        this.isConnected &&
        this.connectedStatus === "connected"
      ) {
        // console.log(this.TCUExistsAlready, this.CCUExistsAlready, this.HMIExistsAlready);
        if (
          !this.TCUExistsAlready &&
          !this.CCUExistsAlready &&
          !this.HMIExistsAlready
        ) {
          setTimeout(() => {
            // console.log('calling updateAlarmLog');
            this.updateFirestoreDevice();
            this.updateAlarms();
            this.updateAlarmLog();
            this.updateFireStoreParameterValues();
          }, 2000);
          // this.updateFireStoreStringParameterValues();
          // this.updateServices();
          let forceFlagCopy = this.forceFlag;
          setTimeout(() => {
            if (forceFlagCopy) {
              this.forceFlag = false;
            }
          }, 2000);
        }
      }
    });
    // console.log('before FirestoreService.updateFireStorePeriodicValues ');
    setTimeout(() => {
      // console.log(this.tcuAddress, this.ccuAddress, this.hmiAddress, this.isConnected, this.connectedStatus);
      if (
        (this.tcuAddress || this.ccuAddress || this.hmiAddress) &&
        this.isConnected &&
        this.connectedStatus === "connected"
      ) {
        // console.log('FirestoreService.updateFireStorePeriodicValues');
        this.updateFireStorePeriodicValues();
      }
    }, 10000);

    this.sendLiveData(86400000);
  }

  public cancelFirestoreLink() {
    this.linkStop$?.next();
    this.linkStop$?.complete();
    this.updateFirebaseSubscription?.unsubscribe();
    this.updateFirebaseSubscription = undefined;
    this.connectionSubs.unsubscribe();
    this.connectionSubs = new Subscription();
  }

  private sendLiveData(ms: number) {
    // console.log('sendLiveData');
    this.firebaseInterval = interval(ms)
      .pipe(takeWhile(() => this.isConnected))
      .subscribe(() => {
        if (
          (this.tcuAddress || this.ccuAddress || this.hmiAddress) &&
          this.connectedStatus === "connected"
        ) {
          this.updateFireStorePeriodicValues();
        }
      });
  }

  public startLiveData(): void {
    //console.log('live session started');
    this.firebaseInterval?.unsubscribe();
    this.sendLiveData(50);
    setTimeout(() => {
      // console.log('live session ended');
      this.firebaseInterval?.unsubscribe();
      this.sendLiveData(86400000);
    }, 60000 * 15);
  }

  public endLiveData(): void {
    // console.log('live session ended');
    this.firebaseInterval?.unsubscribe();
    // this.sendLiveData(86400000);
  }

  private updateAlarms(): void {
    let alarms = this.alarmQuery.alarms;

    if (
      this.forceFlag ||
      JSON.stringify(alarms) !== JSON.stringify(this.oldAlarms)
    ) {
      JSON.parse(JSON.stringify(alarms));
      var tcuAlarms = alarms.filter((x) => x.ecuId === 1);
      var ccuAlarms = alarms.filter((x) => x.ecuId !== 1);

      if (this.tcuAddress) {
        // console.log('UPLOADING TCU ALARMS');
        this.angularFirestore
          .collection(CONFIG.firebaseCollection.devices)
          .doc(this.tcuAddress)
          .collection(CONFIG.firebaseCollection.alarms)
          .doc(CONFIG.firebaseCollection.activeAlarms)
          .set({ alarms: tcuAlarms });
      }

      if (this.ccuAddress) {
        // console.log('UPLOADING CCU ALARMS');
        this.angularFirestore
          .collection(CONFIG.firebaseCollection.devices)
          .doc(this.ccuAddress)
          .collection(CONFIG.firebaseCollection.alarms)
          .doc(CONFIG.firebaseCollection.activeAlarms)
          .set({ alarms: ccuAlarms });
      }

      // TODO: TA BORT KODEN NEDAN VILKET ÄR EN GAMMAL LÖSNING PÅ ALARMUPPLADDNING SOM TOG FÖR MYCKET RESURSER I FIREBASE
      // for (const alarm of alarms) {
      //   this.angularFirestore
      //     .collection(CONFIG.firebaseCollection.devices)
      //     .doc(this.address)
      //     .collection(CONFIG.firebaseCollection.alarms)
      //     .doc(CONFIG.firebaseCollection.previousAlarms)
      //     .collection(CONFIG.firebaseCollection.alarms)
      //     .doc(`${alarm.ecuId}-${alarm.errorCodeId}`)
      //     .set(alarm);
      // }

      this.oldAlarms = [...alarms];
    }
  }

  public updateLastUpdated(
    fw: UpgradeFwStatusEvent,
    fwFile: IFirmwareFile,
    ecuNameAsString: string,
  ) {
    var ecuId = fw["ecuId"];
    var ecuName = ecuNameAsString ?? ecuId.toString();
    if (ecuId === 1 && this.tcuAddress) {
      this.setLastUpdatedForDevice(this.tcuAddress, ecuName, fwFile);
    } else if (ecuId === 6 && this.hmiAddress) {
      this.setLastUpdatedForDevice(this.hmiAddress, ecuName, fwFile);
    } else if (ecuId && this.ccuAddress) {
      this.setLastUpdatedForDevice(this.ccuAddress, ecuName, fwFile);
    }
  }

  public updateLatestConnection(uid: string) {
    // console.log(' updateLatestConnection called');

    if (uid) {
      this.angularFirestore
        .collection(CONFIG.firebaseCollection.users)
        .doc(uid)
        .update({
          hardwareVersions: "",
          connectedDevices: "",
        });
    }
  }

  public updateLastConnected(address: string, uid: string, date: Date) {
    if (address) {
      this.angularFirestore
        .collection(CONFIG.firebaseCollection.devices)
        .doc<ICurrentDevice>(address)
        .update({ lastConnected: date });
    }

    if (address && uid) {
      this.angularFirestore
        .collection(CONFIG.firebaseCollection.users)
        .doc(uid)
        .collection(CONFIG.firebaseCollection.previousDevices)
        .doc<ICurrentDevice>(address)
        .update({ lastConnected: date });
    }
  }

  private updateAlarmLog(): void {
    let alarmLog = this.alarmQuery.alarmLog;
    // console.log('updateAlarmLog', alarmLog);

    if (
      this.forceFlag ||
      JSON.stringify(alarmLog) !== JSON.stringify(this.oldAlarmLog)
    ) {
      JSON.parse(JSON.stringify(alarmLog));
      var tcuAlarms = alarmLog.filter((x) => x.ecuId === 1);
      var ccuAlarms = alarmLog.filter((x) => x.ecuId !== 1);

      if (this.tcuAddress) {
        //console.log('UPLOADING TCU ALARM LOG');
        this.angularFirestore
          .collection(CONFIG.firebaseCollection.devices)
          .doc(this.tcuAddress)
          .collection(CONFIG.firebaseCollection.alarms)
          .doc(CONFIG.firebaseCollection.alarmLog)
          .set({ alarmLog: tcuAlarms });
      }

      if (this.ccuAddress) {
        //  console.log('UPLOADING CCU ALARM LOG');
        this.angularFirestore
          .collection(CONFIG.firebaseCollection.devices)
          .doc(this.ccuAddress)
          .collection(CONFIG.firebaseCollection.alarms)
          .doc(CONFIG.firebaseCollection.alarmLog)
          .set({ alarmLog: ccuAlarms });
      }

      this.oldAlarmLog = [...alarmLog];
    }
  }

  private updateFireStoreParameterValues(): void {
    const parameterData = this.deviceQuery.parameterData;
    const stringParameterData = this.deviceQuery.stringParameterData;
    const { currentDevice } = this.deviceQuery;

    if (
      (this.forceFlag && parameterData?.length > 0) ||
      (parameterData?.length > 0 &&
        JSON.stringify(parameterData) !==
          JSON.stringify(this.oldParameterData)) ||
      (stringParameterData?.length > 0 &&
        JSON.stringify(stringParameterData) !==
          JSON.stringify(this.oldStringParameterData) &&
        currentDevice)
    ) {
      var tcuParameters = parameterData.filter((x) => x.ecuId === 1);
      var hmiParameters = parameterData.filter((x) => x.ecuId === 6);
      var ccuParameters = parameterData.filter(
        (x) => x.ecuId !== 1 && x.ecuId !== 6,
      );

      var tcuSparePartFlag = parameterData.find((x) => x.parameterId === 1151);
      var ccuSparePartFlag = parameterData.find((x) => x.parameterId === 2162);
      var hmiSparePartFlag = parameterData.find((x) => x.parameterId === 6107);

      if (this.tcuAddress && tcuSparePartFlag?.value === 0) {
        // console.log('UPLOADING TCU PARAMETERS');
        this.angularFirestore
          .collection(CONFIG.firebaseCollection.devices)
          .doc(this.tcuAddress)
          .collection(CONFIG.firebaseCollection.parameterData)
          .doc(CONFIG.firebaseCollection.parameterData)
          .set({
            parameterData: JSON.parse(JSON.stringify(tcuParameters)),
          });
      }

      if (this.ccuAddress && ccuSparePartFlag?.value === 0) {
        // console.log('UPLOADING CCU PARAMETERS');
        this.angularFirestore
          .collection(CONFIG.firebaseCollection.devices)
          .doc(this.ccuAddress)
          .collection(CONFIG.firebaseCollection.parameterData)
          .doc(CONFIG.firebaseCollection.parameterData)
          .set({
            parameterData: JSON.parse(JSON.stringify(ccuParameters)),
          });
      }

      if (this.hmiAddress && hmiSparePartFlag?.value === 0) {
        // console.log('UPLOADING CCU PARAMETERS');
        this.angularFirestore
          .collection(CONFIG.firebaseCollection.devices)
          .doc(this.hmiAddress)
          .collection(CONFIG.firebaseCollection.parameterData)
          .doc(CONFIG.firebaseCollection.parameterData)
          .set({
            parameterData: JSON.parse(JSON.stringify(hmiParameters)),
            stringParameterData: JSON.parse(
              JSON.stringify(stringParameterData),
            ),
          });
      }

      // for (const data of parameterData) {
      //   this.angularFirestore
      //     .collection(CONFIG.firebaseCollection.devices)
      //     .doc(this.address)
      //     .collection(CONFIG.firebaseCollection.parameterData)
      //     .doc<IParameterData>(`${data.ecuId}-${data.parameterId}`)
      //     .set(JSON.parse(JSON.stringify(data)));
      // }
      this.oldParameterData = [...parameterData];
      this.oldStringParameterData = [...stringParameterData];
    }
  }

  // private updateFireStoreStringParameterValues(): void {
  //   const stringParameterData = this.deviceQuery.stringParameterData;

  //   if (stringParameterData.length > 0 && JSON.stringify(stringParameterData) !== JSON.stringify(this.oldStringParameterData)) {
  //     this.angularFirestore
  //       .collection(CONFIG.firebaseCollection.devices)
  //       .doc(this.address)
  //       .collection(CONFIG.firebaseCollection.parameterData)
  //       .doc(CONFIG.firebaseCollection.parameterData)
  //       .update({ stringParameterData: JSON.parse(JSON.stringify(stringParameterData)) });

  //     // for (const data of parameterData) {
  //     //   this.angularFirestore
  //     //     .collection(CONFIG.firebaseCollection.devices)
  //     //     .doc(this.address)
  //     //     .collection(CONFIG.firebaseCollection.parameterData)
  //     //     .doc<IParameterData>(`${data.ecuId}-${data.parameterId}`)
  //     //     .set(JSON.parse(JSON.stringify(data)));
  //     // }
  //     this.oldStringParameterData = [...stringParameterData];
  //   }
  // }

  private updateFireStorePeriodicValues(): void {
    //ÄNDRAT FÖR ATT KUNNA KÖRA 100 ms
    // console.log('updated periodic data');
    const periodicData = this.deviceQuery.periodicData;
    var tcuPeriodicData = periodicData.filter((x) => x.ecuId === 1);

    var ccuPeriodicData = periodicData.filter((x) => x.ecuId === 2);
    var hmiPeriodicData = periodicData.filter((x) => x.ecuId === 6);

    if (tcuPeriodicData && this.databaseRefPeriodicDataTcu) {
      // console.log('updateFireStorePeriodicValues', tcuPeriodicData, this.databaseRefPeriodicDataTcu);
      set(this.databaseRefPeriodicDataTcu, tcuPeriodicData);
    }
    if (ccuPeriodicData && this.databaseRefPeriodicDataCcu) {
      set(this.databaseRefPeriodicDataCcu, ccuPeriodicData);
    }
    if (hmiPeriodicData && this.databaseRefPeriodicDataHmi) {
      set(this.databaseRefPeriodicDataHmi, hmiPeriodicData);
    }

    /*this.databaseRefPeriodicDataTcu?.set(tcuPeriodicData);
    this.databaseRefPeriodicDataCcu?.set(ccuPeriodicData);*/
  }

  // private updateServices(): void {
  //   const services = this.deviceQuery.currentDevice.services;

  //   if (services.length > 0 && JSON.stringify(services) !== JSON.stringify(this.oldServices)) {
  //     for (const service of services) {
  //       this.angularFirestore
  //         .collection(CONFIG.firebaseCollection.devices)
  //         .doc(this.address)
  //         .collection(CONFIG.firebaseCollection.services)
  //         .doc(service.service)
  //         .set(service);
  //     }
  //     this.oldServices = [...services];
  //   }
  // }

  public uploadDeviceData(
    userUid: string,
    address: string,
    data: IHardwareVersion,
  ) {
  /*  console.log(
      `UPLOADING DATA FROM ECU: ${data?.ecuId},  ${address},  ${userUid}`,
    );*/

    if (address && data && userUid) {
      // console.log(`UPLOADING DATA FROM ECU: ${data?.ecuId} , ${address},  ${userUid}`);
      try {
        this.angularFirestore
          .collection(CONFIG.firebaseCollection.devices)
          .doc(address)
          .set({ ...data, lastConnected: new Date() }, { merge: true });

        this.angularFirestore
          .collection(CONFIG.firebaseCollection.users)
          .doc(userUid)
          .collection(CONFIG.firebaseCollection.previousDevices)
          .doc<IHardwareVersion>(address)
          .set({ ...data, lastConnected: new Date() }, { merge: true });

        return Promise.resolve("ok");
      } catch (error) {
        return Promise.reject(
          `Something went wrong with upload to firebase: ${error}`,
        );
      }
    } else {
      const errorMessage = `Some of the following data was missing. userUid: ${userUid}, address: ${address}, data: ${data}`;
      console.error(errorMessage);
      return Promise.reject(errorMessage);
    }
  }

  public async updateFirestoreDevice(): Promise<void> {
    const { userUid } = this.userQuery;
    const { currentDevice, hardwareVersions, parameterData } = this.deviceQuery;

    // console.log("tcuaddress", currentDevice?.tcuAddress);
    const tcu = hardwareVersions?.find((x) => x.ecuId === 1);

    var tcuData = await this.handleFirstTcuUpload(tcu);

    tcuData = {
      ...tcuData,
      ccuSerialNumber: currentDevice?.ccuAddress
        ? currentDevice?.ccuAddress
        : "",
      hmiSerialNumber: currentDevice?.hmiAddress
        ? currentDevice?.hmiAddress
        : "",
    };
    // console.log("tcuData", tcuData);
    const ccu = hardwareVersions?.find((x) => x.ecuId === 2);

    
    var ccuData = await this.handleFirstCcuUpload(ccu);
    ccuData = {
      ...ccuData,
      tcuSerialNumber: currentDevice?.tcuAddress
        ? currentDevice?.tcuAddress
        : "",
      hmiSerialNumber: currentDevice?.hmiAddress
        ? currentDevice?.hmiAddress
        : "",
    };

    const hmi = hardwareVersions?.find((x) => x.ecuId === 6);

    var hmiData = await this.handleFirstHmiUpload(hmi);
    hmiData = {
      ...hmiData,
      tcuSerialNumber: currentDevice?.tcuAddress
        ? currentDevice?.tcuAddress
        : "",
      ccuSerialNumber: currentDevice?.ccuAddress
        ? currentDevice?.ccuAddress
        : "",
    };
    var tcuSparePartFlag = parameterData?.find((x) => x.parameterId === 1151);
    var ccuSparePartFlag = parameterData?.find((x) => x.parameterId === 2162);
    var hmiSparePartFlag = parameterData?.find((x) => x.parameterId === 6107);

    if (
      (this.forceFlag && hardwareVersions) ||
      (hardwareVersions &&
        JSON.stringify(hardwareVersions) !==
          JSON.stringify(this.oldHardwareVersions)) ||
      (currentDevice &&
        JSON.stringify(currentDevice) !== JSON.stringify(this.oldCurrentDevice))
    ) {
      if (
        this.tcuAddress &&
        userUid &&
        tcuData &&
        tcuSparePartFlag?.value === 0
      ) {
        /* console.log(
          "here in the condition this.tcuAddress && userUid && tcuData && tcuSparePartFlag?.value === 0 ",
        );*/
        this.uploadDeviceData(userUid, this.tcuAddress, {
          ...tcuData,
          isSparePart: false,
        }).then(() => {
          this.firstTcuUploadDone = true;
          //console.log("firstTcuUploadDone", this.firstTcuUploadDone);
        });
      }

      if (
        this.ccuAddress &&
        userUid &&
        ccuData &&
        ccuSparePartFlag?.value === 0
      ) {
        this.uploadDeviceData(userUid, this.ccuAddress, {
          ...ccuData,
          isSparePart: false,
        }).then(() => {
          this.firstCcuUploadDone = true;
          // console.log('firstCcuUploadDone', this.firstCcuUploadDone);
        });
      }

      if (
        this.hmiAddress &&
        userUid &&
        hmiData &&
        hmiSparePartFlag?.value === 0
      ) {
        this.uploadDeviceData(userUid, this.hmiAddress, {
          ...hmiData,
          isSparePart: false,
        }).then(() => {
          this.firstHmiUploadDone = true;
          // console.log('firstHmiUploadDone', this.firstHmiUploadDone);
        });
      }

      //console.log('UPLOADING USER DATA');

      if (
        userUid &&
        hardwareVersions &&
        hardwareVersions?.length > 0 &&
        currentDevice
      ) {
        this.angularFirestore
          .collection(CONFIG.firebaseCollection.users)
          .doc(userUid)
          .set(
            {
              hardwareVersions: hardwareVersions,
              latestConnection: currentDevice,
            },
            { merge: true },
          );

        this.oldHardwareVersions = hardwareVersions;
        this.oldCurrentDevice = currentDevice;
      }
    }

    var now = new Date().getTime();

    if (this.tcuAddress) {
      set(this.databaseRefLastConnectedTcu, now);

      set(
        this.databaseRefLastConnectedUserTcu,
        this.userQuery.userUid ?? "This device has no previous connection",
      );
    }

    if (this.ccuAddress) {
      set(this.databaseRefLastConnectedCcu, now);

      set(
        this.databaseRefLastConnectedUserCcu,
        this.userQuery.userUid ?? "This device has no previous connection",
      );
    }

    if (this.hmiAddress) {
      set(this.databaseRefLastConnectedHmi, now);

      set(
        this.databaseRefLastConnectedUserHmi,
        this.userQuery.userUid ?? "This device has no previous connection",
      );
    }
  }

  private async handleFirstTcuUpload(hardwareVersion: IHardwareVersion) {
    //  console.log("i am in tcuaddress handel");
    var data!: IHardwareVersion;
    if (hardwareVersion) {
      if (!this.firstTcuUploadDone && this.tcuAddress) {
        // console.log(this.tcuAddress);
        const docSnapshot = await lastValueFrom(
          this.angularFirestore
            .collection(CONFIG.firebaseCollection.devices)
            .doc(this.tcuAddress)
            .get(),
        );

        if (!docSnapshot.exists) {
          data = {
            ...hardwareVersion,
            address: this.tcuAddress?.split("-")[0],
            installationDate: new Date(),
            lastConnected: new Date(),
          };
        } else {
          var cloudData = docSnapshot.data() as IHardwareVersion;
          data = {
            ...cloudData,
            ...hardwareVersion,
            address: this.tcuAddress?.split("-")[0],
          };
        }
      } else {
        data = { ...hardwareVersion, address: this.tcuAddress?.split("-")[0] };
      }
    }
    //  console.log("data", data);
    return data;
  }

  private async handleFirstCcuUpload(hardwareVersion: IHardwareVersion) {
    var data!: IHardwareVersion;
    // console.log('CCU', hardwareVersion);
    if (hardwareVersion) {
      if (!this.firstCcuUploadDone && this.ccuAddress) {
        const docSnapshot = await lastValueFrom(
          this.angularFirestore
            .collection(CONFIG.firebaseCollection.devices)
            .doc(this.ccuAddress)
            .get(),
        );

        if (!docSnapshot.exists) {
          data = {
            ...hardwareVersion,
            address: this.ccuAddress.split("-")[0],
            installationDate: new Date(),
            lastConnected: new Date(),
          };
        } else {
          var cloudData = docSnapshot.data() as IHardwareVersion;
          data = {
            ...cloudData,
            address: this.ccuAddress.split("-")[0],
            ...hardwareVersion,
          };
        }
      } else {
        data = { ...hardwareVersion, address: this.ccuAddress?.split("-")[0] };
      }
    }

    return data;
  }

  private async handleFirstHmiUpload(hardwareVersion: IHardwareVersion) {
    var data!: IHardwareVersion;
    // console.log('HMI', hardwareVersion);
    if (hardwareVersion) {
      if (!this.firstHmiUploadDone && this.hmiAddress) {
        const docSnapshot = await lastValueFrom(
          this.angularFirestore
            .collection(CONFIG.firebaseCollection.devices)
            .doc(this.hmiAddress)
            .get(),
        );
        if (!docSnapshot.exists) {
          data = {
            ...hardwareVersion,
            address: this.hmiAddress,
            installationDate: new Date(),
            lastConnected: new Date(),
          };
        } else {
          var cloudData = docSnapshot.data() as IHardwareVersion;
          data = { ...cloudData, address: this.hmiAddress, ...hardwareVersion };
        }
      } else {
        data = { ...hardwareVersion, address: this.hmiAddress };
      }
    }

    return data;
  }

  public updateLatestTcuConnection(serialNumber: string) {
    // console.log('tcu', serialNumber)
    if (this.userQuery.userUid) {
      this.angularFirestore
        .collection(CONFIG.firebaseCollection.users)
        .doc(this.userQuery.userUid)
        .update({ latestTcuConnection: serialNumber });
    }
  }

  public updateLatestCcuConnection(serialNumber: string) {
    if (this.userQuery.userUid) {
      this.angularFirestore
        .collection(CONFIG.firebaseCollection.users)
        .doc(this.userQuery.userUid)
        .update({ latestCcuConnection: serialNumber });
    }
  }

  public updateLatestHmiConnection(serialNumber: string) {
    if (this.userQuery.userUid) {
      this.angularFirestore
        .collection(CONFIG.firebaseCollection.users)
        .doc(this.userQuery.userUid)
        .update({ latestHmiConnection: serialNumber });
    }
  }

  private setLastUpdatedForDevice(
    address: string,
    ecuName: string,
    fwFile: IFirmwareFile,
  ) {
    this.angularFirestore
      .collection(CONFIG.firebaseCollection.devices)
      .doc(address)
      .collection(CONFIG.firebaseCollection.lastUpdated)
      .doc(ecuName)
      .set({
        lastUpdated: new Date(),
        user: this.userQuery.name,
        userId: this.userQuery.userUid,
        firmwareVersion: fwFile.buildNumber,
      });
  }
  /*
  public async fetchBackupParametersForSerialNumber(
    serialNumber: string,
    ecuId: number,
    useFactorySettings: boolean,
    currentHardwareNumber: string,
  ) {
    console.log(serialNumber, ecuId, currentHardwareNumber, useFactorySettings);

    if (serialNumber) {
      var unitFound = await lastValueFrom(
        this.angularFirestore
          .collection(CONFIG.firebaseCollection.devices, (ref) =>
            ref.where("address", "==", serialNumber),
          )
          .get(),
      );
      if (unitFound) {
        var maped = unitFound.docs.map((y) => ({
          ...(y.data() as any),
          uid: y.id,
        }));

        let unitData;

        var unitDataList = maped.filter(
          (dev: IHardwareVersion) =>
            !dev.isSparePart &&
            (!dev.isInactive ||
              (ecuId === 6
                ? dev.uid === serialNumber
                : dev.uid === `${serialNumber}-${currentHardwareNumber}`)),
        ) as IHardwareVersion[];
        if (unitDataList && unitDataList.length > 0) {
          if (unitDataList.length > 1) {
            unitData = unitDataList.find((unit) =>
              ecuId === 6
                ? unit.uid === serialNumber
                : unit.uid === `${serialNumber}-${currentHardwareNumber}`,
            );
          } else {
            unitData = unitDataList[0];
          }
        }

        if (unitData?.ecuId === ecuId) {
          var backupParameters = {
            parameterData: await lastValueFrom(
              this.angularFirestore
                .collection(
                  useFactorySettings
                    ? CONFIG.firebaseCollection.backups
                    : CONFIG.firebaseCollection.devices,
                )
                .doc(useFactorySettings ? `${serialNumber}-0` : unitData.uid)
                .collection(
                  useFactorySettings
                    ? CONFIG.firebaseCollection.backupParameters
                    : CONFIG.firebaseCollection.parameterData,
                )
                .doc(CONFIG.firebaseCollection.parameterData)
                .get(),
            ),
            deviceData: unitData,
            unitsToDeactivate: maped as IHardwareVersion[],
          };
          if (backupParameters.parameterData.exists) {
            return backupParameters;
          } else {
            return Promise.reject();
          }
        }
      } else if (useFactorySettings && !unitFound) {
        console.log("no unit find");
        var backupParametersFactory = {
          parameterData: await lastValueFrom(
            this.angularFirestore
              .collection(CONFIG.firebaseCollection.backups)
              .doc(`${serialNumber}-0`)
              .collection(CONFIG.firebaseCollection.backupParameters)
              .doc(CONFIG.firebaseCollection.parameterData)
              .get(),
          ),
          deviceData: null,
          unitsToDeactivate: null,
        };
        console.log("backupParametersFactory", backupParametersFactory);
        if (backupParametersFactory.parameterData.exists) {
          return backupParametersFactory;
        } else {
          return Promise.reject();
        }
      } else {
        return Promise.reject();
      }
    } else {
      return Promise.reject();
    }
  }
*/
  public async fetchBackupParametersForSerialNumber(
    serialNumber: string,
    ecuId: number,
    useFactorySettings: boolean,
    currentHardwareNumber: string,
  ): Promise<{
    parameterData: any;
    deviceData: IHardwareVersion | null;
    unitsToDeactivate: IHardwareVersion[] | null;
  }> {
    //  console.log(serialNumber, ecuId, currentHardwareNumber, useFactorySettings);

    if (!serialNumber) {
      return Promise.reject("Missing serial number");
    }

    const unitFound = await lastValueFrom(
      this.angularFirestore
        .collection(CONFIG.firebaseCollection.devices, (ref) =>
          ref.where("address", "==", serialNumber),
        )
        .get(),
    );

    const mapped = unitFound.docs.map((doc) => ({
      ...(doc.data() as any),
      uid: doc.id,
    })) as IHardwareVersion[];

    // If factory settings are requested, try to read factory backup directly
    if (useFactorySettings) {
      const factoryParameterData = await lastValueFrom(
        this.angularFirestore
          .collection(CONFIG.firebaseCollection.backups)
          .doc(`${serialNumber}-0`)
          .collection(CONFIG.firebaseCollection.backupParameters)
          .doc(CONFIG.firebaseCollection.parameterData)
          .get(),
      );

      if (factoryParameterData.exists) {
        return {
          parameterData: factoryParameterData,
          deviceData: null,
          unitsToDeactivate: mapped.length > 0 ? mapped : null,
        };
      }

      return Promise.reject("Factory backup parameters not found");
    }

    // Normal device lookup
    if (unitFound.empty) {
      return Promise.reject("No unit found");
    }

    const unitDataList = mapped.filter(
      (dev: IHardwareVersion) =>
        !dev.isSparePart &&
        (!dev.isInactive ||
          (ecuId === 6
            ? dev.uid === serialNumber
            : dev.uid === `${serialNumber}-${currentHardwareNumber}`)),
    );

    let unitData: IHardwareVersion | undefined;

    if (unitDataList.length > 1) {
      unitData = unitDataList.find((unit) =>
        ecuId === 6
          ? unit.uid === serialNumber
          : unit.uid === `${serialNumber}-${currentHardwareNumber}`,
      );
    } else {
      unitData = unitDataList[0];
    }

    if (!unitData) {
      return Promise.reject("Matching unit data not found");
    }

    if (unitData.ecuId !== ecuId) {
      return Promise.reject("ECU ID mismatch");
    }

    const parameterData = await lastValueFrom(
      this.angularFirestore
        .collection(CONFIG.firebaseCollection.devices)
        .doc(unitData.uid)
        .collection(CONFIG.firebaseCollection.parameterData)
        .doc(CONFIG.firebaseCollection.parameterData)
        .get(),
    );

    if (!parameterData.exists) {
      return Promise.reject("Device parameters not found");
    }

    return {
      parameterData,
      deviceData: unitData,
      unitsToDeactivate: mapped,
    };
  }
  public setOldDeviceAsSparePart(device: IHardwareVersion) {
    this.angularFirestore
      .collection(CONFIG.firebaseCollection.devices)
      .doc(device.uid)
      .update({ isSparePart: true });
  }
  /*
  public  setDeviceAsInactive(device: IHardwareVersion) {
    if (device) {
      this.angularFirestore
        .collection(CONFIG.firebaseCollection.devices)
        .doc(device.uid)
        .update({ isInactive: true });
      if (device.ecuName === "TCU") {
        this.TCUExistsAlready = false;
      } else if (device.ecuName === "CCU") {
        this.CCUExistsAlready = false;
      } else if (device.ecuName === "HMI") {
        this.HMIExistsAlready = false;
      }
    } else {
      console.error("Could not find device to set as inactive");
    }
  }*/
  public async setDeviceAsInactive(device: IHardwareVersion): Promise<void> {
    if (!device?.uid) {
      throw new Error("Could not find device to set as inactive");
    }

    await this.angularFirestore
      .collection(CONFIG.firebaseCollection.devices)
      .doc(device.uid)
      .update({ isInactive: true });

    if (device.ecuName === "TCU") {
      this.TCUExistsAlready = false;
    } else if (device.ecuName === "CCU") {
      this.CCUExistsAlready = false;
    } else if (device.ecuName === "HMI") {
      this.HMIExistsAlready = false;
    }
  }
  /*
  public setDeviceAsActive(device: IHardwareVersion) {
    if (device) {
      this.angularFirestore
        .collection(CONFIG.firebaseCollection.devices)
        .doc(device.uid)
        .update({ isInactive: false });
    } else {
      console.error("Could not find device to set as active");
    }
  }*/

  public async setDeviceAsActive(device: IHardwareVersion): Promise<void> {
    if (!device?.uid) {
      throw new Error("Could not find device to set as active");
    }

    await this.angularFirestore
      .collection(CONFIG.firebaseCollection.devices)
      .doc(device.uid)
      .set(
        {
          ...device,
          isInactive: false,
          isSparePart: false,
        },
        { merge: true },
      );
  }

  private handleIfDeviceBecomesInactive(address: string) {
    //console.log('address', address);
    return this.angularFirestore
      .collection(CONFIG.firebaseCollection.devices)
      .doc<IHardwareVersion>(address)
      .valueChanges();
  }

  public logBackup(
    oldSerialNumber: string,
    newSerialNumber: string,
    typeOfUnit: string,
  ) {
    var user = this.userQuery.currentUser;
    return this.angularFirestore
      .collection(CONFIG.firebaseCollection.logs)
      .add({
        user: `${user?.firstName ?? "Could not find users first name"} ${user?.lastName ?? "Could not users last name"}`,
        email: user.email ?? "Could not find users email",
        date: new Date(),
        oldSerialNumber: newSerialNumber ?? "Could not find old serial number",
        newSerialNumber: oldSerialNumber ?? "Could not find new serial number",
        typeOfUnit: typeOfUnit ?? "Could not find which type of unit",
      });
  }

  public async checkForDuplicateTcu(serialNumber: string) {
    const address = serialNumber.split("-")[0]; // "1245"
    // console.log('Checking address prefix:', serialNumber, address);

    const getRequest$ = this.angularFirestore
      .collection(CONFIG.firebaseCollection.devices, (ref) =>
        ref.where("address", "==", address),
      )
      .get();

    const snapshot = await lastValueFrom(getRequest$);

    const mapped = snapshot.docs.map((doc) => ({
      ...(doc.data() as any),
      uid: doc.id,
    }));
    // console.log('Mapped devices:', mapped);

    if (mapped.length > 0) {
      // Find if there’s any unit that matches but is not the same as the current serialNumber
      const duplicateUnit = mapped.find(
        (unit) =>
          unit.uid !== serialNumber &&
          (unit.isInactive === false || unit.isInactive === undefined) &&
          unit.isSparePart === false,
      );

      if (duplicateUnit) {
        // console.log('Found duplicate TCU:', duplicateUnit);
        this.TCUExistsAlready = true;
        this.duplicateDeviceDetected$.next(duplicateUnit);
      } else {
        this.TCUExistsAlready = false;
      }
    } else {
      // console.log('No TCU found with this prefix. Proceed normally.');
      this.TCUExistsAlready = false;
    }
  }

  public async checkForDuplicateCcu(serialNumber: string) {
    const address = serialNumber.split("-")[0]; // "1245"
    // console.log('Checking address prefix:', serialNumber, address);

    const getRequest$ = this.angularFirestore
      .collection(CONFIG.firebaseCollection.devices, (ref) =>
        ref.where("address", "==", address),
      )
      .get();

    const snapshot = await lastValueFrom(getRequest$);

    const mapped = snapshot.docs.map((doc) => ({
      ...(doc.data() as any),
      uid: doc.id,
    }));
    // console.log('Mapped devices:', mapped);

    if (mapped.length > 0) {
      // Find if there’s any unit that matches but is not the same as the current serialNumber
      const duplicateUnit = mapped.find(
        (unit) =>
          unit.uid !== serialNumber &&
          (unit.isInactive === false || unit.isInactive === undefined) &&
          unit.isSparePart === false,
      );

      if (duplicateUnit) {
        // console.log('Found duplicate CCU:', duplicateUnit);
        this.CCUExistsAlready = true;
        this.duplicateDeviceDetected$.next(duplicateUnit);
      } else {
        this.CCUExistsAlready = false;
      }
    } else {
      //console.log('No CCU found with this prefix. Proceed normally.');

      this.CCUExistsAlready = false;
    }
  }
  public async checkForDuplicateHmi(serialNumber: string) {
    const address = serialNumber;
    // console.log('Checking address prefix:', serialNumber, address);

    const getRequest$ = this.angularFirestore
      .collection(CONFIG.firebaseCollection.devices, (ref) =>
        ref.where("address", "==", address),
      )
      .get();

    const snapshot = await lastValueFrom(getRequest$);

    const mapped = snapshot.docs.map((doc) => ({
      ...(doc.data() as any),
      uid: doc.id,
    }));
    // console.log('Mapped devices:', mapped);

    if (mapped.length > 0) {
      // Find if there’s any unit that matches but is not the same as the current serialNumber
      const duplicateUnit = mapped.find(
        (unit) =>
          unit.uid !== serialNumber &&
          (unit.isInactive === false || unit.isInactive === undefined) &&
          unit.isSparePart === false,
      );

      if (duplicateUnit) {
        // console.log('Found duplicate HMI:', duplicateUnit);
        this.HMIExistsAlready = true;
        this.duplicateDeviceDetected$.next(duplicateUnit);
      } else {
        this.HMIExistsAlready = false;
      }
    } else {
      // console.log('No HMI found with this prefix. Proceed normally.');

      this.HMIExistsAlready = false;
      // this.setDatabaseReferences(unitType);
    }
  }

  public activateDuplicateFlag() {
    this.TCUExistsAlready = true;
    this.CCUExistsAlready = true;
    this.HMIExistsAlready = true;
  }
}
